import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Clock, Users, Download, Calendar, Timer, TrendingUp,
  CheckCircle2, AlertCircle, FileSpreadsheet, Trash2, Edit,
  Plus, RefreshCw, Search, MapPin, LogIn, LogOut, Navigation, Loader2,
  Home, Send, XCircle
} from 'lucide-react';

// Centre Location
const CENTRE_LOCATION = {
  latitude: 51.5478,
  longitude: 0.0365,
  radius: 50
};

// Calculate distance between coordinates
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const AttendancePage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [staffSummaries, setStaffSummaries] = useState([]);
  const [currentlyIn, setCurrentlyIn] = useState([]);
  const [myRecords, setMyRecords] = useState([]);
  const [mySummary, setMySummary] = useState(null);
  const [staffList, setStaffList] = useState([]);
  
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStaff, setSelectedStaff] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Permissions state
  const [permissions, setPermissions] = useState({
    can_manual_entry: false,
    can_view_all: false,
    is_super_admin: false
  });
  const [staffPermissions, setStaffPermissions] = useState([]);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [updatingPermission, setUpdatingPermission] = useState(null);
  
  // Quick Clock In/Out state
  const [clockStatus, setClockStatus] = useState(null);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [distance, setDistance] = useState(null);
  const [showClockOutDialog, setShowClockOutDialog] = useState(false);
  const [breakMinutes, setBreakMinutes] = useState(0);
  
  // Remote Work state
  const [remoteApprovalStatus, setRemoteApprovalStatus] = useState(null);
  const [showRemoteRequestDialog, setShowRemoteRequestDialog] = useState(false);
  const [remoteRequestReason, setRemoteRequestReason] = useState('');
  const [submittingRemoteRequest, setSubmittingRemoteRequest] = useState(false);
  const [pendingRemoteRequests, setPendingRemoteRequests] = useState([]);
  const [showPendingRequestsDialog, setShowPendingRequestsDialog] = useState(false);
  
  // Manual Clock Out state
  const [showManualClockOutDialog, setShowManualClockOutDialog] = useState(false);
  const [selectedStaffForClockOut, setSelectedStaffForClockOut] = useState(null);
  const [manualClockOutReason, setManualClockOutReason] = useState('');
  const [manualClockOutBreakMins, setManualClockOutBreakMins] = useState(0);
  const [processingManualClockOut, setProcessingManualClockOut] = useState(false);

  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const canViewAll = permissions.can_view_all || isSuperAdmin;
  const canManualClockOut = ['director', 'managing_director', 'hr', 'admin'].includes(user?.role) || isSuperAdmin;

  // Fetch clock status
  const fetchClockStatus = useCallback(async () => {
    try {
      const res = await api.get('/attendance/status');
      setClockStatus(res.data);
    } catch (err) {
      console.error('Error fetching clock status:', err);
    }
  }, [api]);

  // Fetch permissions
  const fetchPermissions = useCallback(async () => {
    try {
      const res = await api.get('/attendance/my-permissions');
      setPermissions(res.data);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  }, [api]);

  // Fetch remote approval status
  const fetchRemoteApprovalStatus = useCallback(async () => {
    try {
      const res = await api.get('/attendance/remote/my-approval-status');
      setRemoteApprovalStatus(res.data);
    } catch (err) {
      console.error('Error fetching remote approval status:', err);
    }
  }, [api]);

  // Fetch pending remote requests (for approvers)
  const fetchPendingRemoteRequests = useCallback(async () => {
    if (!remoteApprovalStatus?.can_approve_others && !isSuperAdmin) return;
    try {
      const res = await api.get('/attendance/remote/pending-requests');
      setPendingRemoteRequests(res.data.requests || []);
    } catch (err) {
      console.error('Error fetching pending requests:', err);
    }
  }, [api, remoteApprovalStatus, isSuperAdmin]);

  // Submit remote work request
  const submitRemoteRequest = async () => {
    if (!remoteRequestReason.trim()) {
      toast.error('Please provide a reason for working remotely');
      return;
    }
    try {
      setSubmittingRemoteRequest(true);
      await api.post('/attendance/remote/request-approval', { reason: remoteRequestReason });
      toast.success('Remote work request submitted. Awaiting approval.');
      setShowRemoteRequestDialog(false);
      setRemoteRequestReason('');
      fetchRemoteApprovalStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingRemoteRequest(false);
    }
  };

  // Approve/Reject remote request
  const handleRemoteRequestAction = async (requestId, action, validDays = 30) => {
    try {
      if (action === 'approve') {
        await api.post('/attendance/remote/approve-request', { request_id: requestId, valid_days: validDays });
        toast.success('Remote work request approved');
      } else {
        await api.post('/attendance/remote/reject-request', { request_id: requestId });
        toast.success('Remote work request rejected');
      }
      fetchPendingRemoteRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process request');
    }
  };

  // Fetch staff permissions (Super Admin only)
  const fetchStaffPermissions = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.get('/attendance/staff-permissions');
      setStaffPermissions(res.data.staff_permissions || []);
    } catch (err) {
      console.error('Error fetching staff permissions:', err);
    }
  }, [api, isSuperAdmin]);

  // Toggle staff permission
  const toggleStaffPermission = async (userId, permissionType, currentValue) => {
    try {
      setUpdatingPermission(`${userId}-${permissionType}`);
      const data = { user_id: userId };
      
      if (permissionType === 'manual_entry') {
        data.allow_manual_entry = !currentValue;
      } else if (permissionType === 'view_all') {
        data.allow_view_all_records = !currentValue;
      } else if (permissionType === 'excel') {
        data.allow_excel_download = !currentValue;
      }
      
      await api.post('/attendance/set-permission', data);
      toast.success(`Permission ${!currentValue ? 'granted' : 'revoked'}`);
      fetchStaffPermissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update permission');
    } finally {
      setUpdatingPermission(null);
    }
  };

  // Clear permission override
  const clearPermissionOverride = async (userId) => {
    try {
      setUpdatingPermission(userId);
      await api.delete(`/attendance/clear-permission/${userId}`);
      toast.success('Permission reset to role default');
      fetchStaffPermissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear permission');
    } finally {
      setUpdatingPermission(null);
    }
  };

  // Get current GPS location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      setGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setCurrentLocation(loc);
          const dist = calculateDistance(loc.latitude, loc.longitude, CENTRE_LOCATION.latitude, CENTRE_LOCATION.longitude);
          setDistance(Math.round(dist));
          setGettingLocation(false);
          resolve(loc);
        },
        (error) => {
          setGettingLocation(false);
          let msg = 'Unable to get location';
          if (error.code === error.PERMISSION_DENIED) msg = 'Location permission denied';
          reject(new Error(msg));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  // Quick Clock In (one click)
  const handleQuickClockIn = async () => {
    try {
      setClockingIn(true);
      const loc = await getCurrentLocation();
      const res = await api.post('/attendance/clock-in', {
        latitude: loc.latitude,
        longitude: loc.longitude,
        accuracy: loc.accuracy
      });
      toast.success(res.data.message);
      fetchClockStatus();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  // Quick Clock Out (one click)
  const handleQuickClockOut = async () => {
    try {
      setClockingOut(true);
      const loc = await getCurrentLocation();
      const res = await api.post('/attendance/clock-out', {
        latitude: loc.latitude,
        longitude: loc.longitude,
        accuracy: loc.accuracy,
        break_minutes: breakMinutes
      });
      toast.success(`Clocked out! Total: ${res.data.total_hours}h`);
      setShowClockOutDialog(false);
      setBreakMinutes(0);
      fetchClockStatus();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to clock out');
    } finally {
      setClockingOut(false);
    }
  };

  // Format elapsed time
  const formatElapsedTime = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Manual Clock Out Handler - for HR/Admin/Directors
  const handleManualClockOut = async () => {
    if (!selectedStaffForClockOut || !manualClockOutReason.trim()) {
      toast.error('Please select a staff member and provide a reason');
      return;
    }

    setProcessingManualClockOut(true);
    try {
      const res = await api.post('/attendance/manual-clock-out', {
        staff_id: selectedStaffForClockOut.staff_id,
        break_minutes: manualClockOutBreakMins,
        reason: manualClockOutReason.trim()
      });

      toast.success(`${res.data.staff_name} has been clocked out. Total: ${res.data.total_hours}h`);
      setShowManualClockOutDialog(false);
      setSelectedStaffForClockOut(null);
      setManualClockOutReason('');
      setManualClockOutBreakMins(0);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock out staff');
    } finally {
      setProcessingManualClockOut(false);
    }
  };

  // Fetch all attendance data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get who's currently clocked in
      const clockedInRes = await api.get('/attendance/who-is-clocked-in');
      setCurrentlyIn(clockedInRes.data.currently_clocked_in || []);
      
      // Get my records
      const myRes = await api.get(`/attendance/my-records?month=${selectedMonth}&year=${selectedYear}`);
      setMyRecords(myRes.data.records || []);
      setMySummary(myRes.data.summary);
      
      // Get all records if authorized
      if (canViewAll) {
        const allRes = await api.get(`/attendance/all-records?month=${selectedMonth}&year=${selectedYear}${selectedStaff ? `&staff_id=${selectedStaff}` : ''}`);
        setRecords(allRes.data.records || []);
        setStaffSummaries(allRes.data.staff_summaries || []);
        
        // Get staff list for dropdown
        const staffRes = await api.get('/staff');
        setStaffList(staffRes.data || []);
      }
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [api, selectedMonth, selectedYear, selectedStaff, canViewAll]);

  useEffect(() => {
    fetchData();
    fetchClockStatus();
    fetchPermissions();
    fetchRemoteApprovalStatus();
    // Refresh clock status every minute
    const interval = setInterval(fetchClockStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchData, fetchClockStatus, fetchPermissions, fetchRemoteApprovalStatus]);

  // Fetch pending remote requests when approval status loaded
  useEffect(() => {
    if (remoteApprovalStatus?.can_approve_others || isSuperAdmin) {
      fetchPendingRemoteRequests();
    }
  }, [remoteApprovalStatus, isSuperAdmin, fetchPendingRemoteRequests]);

  // Export to Excel
  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.get(
        `/attendance/export-excel?month=${selectedMonth}&year=${selectedYear}${selectedStaff ? `&staff_id=${selectedStaff}` : ''}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_${selectedYear}_${selectedMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel file downloaded');
    } catch (err) {
      toast.error('Failed to export attendance data');
    } finally {
      setExporting(false);
    }
  };

  // Delete record (Super Admin only)
  const handleDelete = async (recordId) => {
    if (!confirm('Are you sure you want to delete this attendance record?')) return;
    
    try {
      await api.delete(`/attendance/record/${recordId}`);
      toast.success('Record deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete record');
    }
  };

  // Update record (Super Admin only)
  const handleUpdate = async () => {
    if (!selectedRecord) return;
    
    try {
      await api.put(`/attendance/record/${selectedRecord.id}`, {
        clock_in_time: selectedRecord.clock_in_time,
        clock_out_time: selectedRecord.clock_out_time,
        break_minutes: selectedRecord.break_minutes,
        total_hours: selectedRecord.total_hours,
        overtime_hours: selectedRecord.overtime_hours
      });
      toast.success('Record updated');
      setShowEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update record');
    }
  };

  // Format time
  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  // Filter records by search
  const filteredRecords = records.filter(r => 
    r.staff_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.staff_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Attendance & Time Tracking</h1>
          <p className="text-slate-500">Track staff clock-in/out and working hours</p>
        </div>
        <div className="flex gap-2">
          {(permissions.can_excel_download || isSuperAdmin) && (
            <Button onClick={handleExport} disabled={exporting} variant="outline">
              {exporting ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2" />
              )}
              Export Excel
            </Button>
          )}
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Clock In/Out - One Click */}
      <Card className={`border-2 ${clockStatus?.is_clocked_in ? 'border-green-400 bg-green-50' : 'border-blue-300 bg-blue-50'}`}>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`p-4 rounded-full ${clockStatus?.is_clocked_in ? 'bg-green-200' : 'bg-blue-200'}`}>
                <Clock className={`w-8 h-8 ${clockStatus?.is_clocked_in ? 'text-green-700' : 'text-blue-700'}`} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">
                  {clockStatus?.is_clocked_in ? 'You are Clocked In' : 'Quick Clock In'}
                </h2>
                {clockStatus?.is_clocked_in ? (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-600">
                      Since: {clockStatus?.clock_in_time && new Date(clockStatus.clock_in_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-lg font-semibold text-green-700">
                      <Timer className="w-4 h-4 inline mr-1" />
                      Working: {formatElapsedTime(clockStatus?.elapsed_hours)}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">
                    Click the button to clock in instantly
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Location Status */}
              {distance !== null && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                  distance <= CENTRE_LOCATION.radius ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {distance}m {distance <= CENTRE_LOCATION.radius ? '(In Range)' : '(Out of Range)'}
                  </span>
                </div>
              )}
              
              {/* Get Location Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={getCurrentLocation}
                disabled={gettingLocation}
                title="Check your location"
              >
                {gettingLocation ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Navigation className="w-4 h-4" />
                )}
              </Button>
              
              {/* Clock In/Out Button */}
              {clockStatus?.is_clocked_in ? (
                <Button
                  onClick={() => setShowClockOutDialog(true)}
                  disabled={clockingOut}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 text-lg"
                  size="lg"
                >
                  {clockingOut ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <LogOut className="w-5 h-5 mr-2" />
                  )}
                  Clock Out
                </Button>
              ) : (
                <Button
                  onClick={handleQuickClockIn}
                  disabled={clockingIn}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 text-lg"
                  size="lg"
                >
                  {clockingIn ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <LogIn className="w-5 h-5 mr-2" />
                  )}
                  Clock In
                </Button>
              )}
            </div>
          </div>
          
          <p className="text-xs text-slate-500 mt-3">
            <MapPin className="w-3 h-3 inline mr-1" />
            Location: Citygate House, 246-250 Romford Road, London E7 9HZ (50m radius required)
          </p>
        </CardContent>
      </Card>

      {/* Remote Work Section */}
      <Card className={`border ${remoteApprovalStatus?.approved ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Home className={`w-6 h-6 ${remoteApprovalStatus?.approved ? 'text-green-600' : 'text-amber-600'}`} />
              <div>
                <h3 className="font-semibold text-slate-800">Remote Work</h3>
                {remoteApprovalStatus?.approved ? (
                  <p className="text-sm text-green-700">
                    {remoteApprovalStatus.reason === 'role_based' 
                      ? 'Your role allows remote clock-in' 
                      : `Approved by ${remoteApprovalStatus.approved_by} ${remoteApprovalStatus.valid_until ? `until ${new Date(remoteApprovalStatus.valid_until).toLocaleDateString('en-GB')}` : ''}`}
                  </p>
                ) : (
                  <p className="text-sm text-amber-700">Pre-approval required to clock in from home</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {!remoteApprovalStatus?.approved && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowRemoteRequestDialog(true)}
                  className="border-amber-400 text-amber-700 hover:bg-amber-100"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Request Approval
                </Button>
              )}
              {(remoteApprovalStatus?.can_approve_others || isSuperAdmin) && (
                <Button 
                  onClick={() => setShowPendingRequestsDialog(true)}
                  className="bg-[#1e3a5f]"
                >
                  <Users className="w-4 h-4 mr-2" />
                  {pendingRemoteRequests.length > 0 ? `Pending Requests (${pendingRemoteRequests.length})` : 'Manage Approvals'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currently Clocked In */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Currently Clocked In
            {currentlyIn.length > 0 && (
              <Badge className="bg-green-600 text-white ml-2">{currentlyIn.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentlyIn.length > 0 ? (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {currentlyIn.map((staff) => (
                  <div 
                    key={staff.staff_id} 
                    className="flex items-center gap-2 bg-white border border-green-300 rounded-full py-1 px-3"
                  >
                    <span className="font-medium text-green-800">{staff.staff_name}</span>
                    <span className="text-xs text-green-600">
                      ({staff.elapsed_hours?.toFixed(1) || 0}h)
                    </span>
                    {canManualClockOut && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full"
                        onClick={() => {
                          setSelectedStaffForClockOut(staff);
                          setShowManualClockOutDialog(true);
                        }}
                        title={`Clock out ${staff.staff_name}`}
                      >
                        <LogOut className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {canManualClockOut && (
                <p className="text-xs text-green-700 mt-2">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Click the logout icon to manually clock out a staff member who missed clocking out
                </p>
              )}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No staff currently clocked in</p>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue={canViewAll ? "all" : "my"}>
        <TabsList>
          <TabsTrigger value="my">My Attendance</TabsTrigger>
          {canViewAll && <TabsTrigger value="all">All Staff</TabsTrigger>}
          {canViewAll && <TabsTrigger value="summary">Summary</TabsTrigger>}
        </TabsList>

        {/* My Attendance Tab */}
        <TabsContent value="my" className="space-y-4">
          {/* My Summary Cards */}
          {mySummary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Days Worked</p>
                      <p className="text-2xl font-bold">{mySummary.total_days_worked}</p>
                    </div>
                    <Calendar className="w-8 h-8 text-[#1e3a5f]" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Hours</p>
                      <p className="text-2xl font-bold">{mySummary.total_hours}h</p>
                    </div>
                    <Timer className="w-8 h-8 text-[#2a9d8f]" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Overtime</p>
                      <p className="text-2xl font-bold text-amber-600">{mySummary.total_overtime_hours}h</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-amber-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* My Records Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Clock In</TableHead>
                    <TableHead>Clock Out</TableHead>
                    <TableHead>Break</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Overtime</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {myRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">{record.date}</TableCell>
                      <TableCell>{formatTime(record.clock_in_time)}</TableCell>
                      <TableCell>{formatTime(record.clock_out_time)}</TableCell>
                      <TableCell>{record.break_minutes}m</TableCell>
                      <TableCell>{record.total_hours || '-'}h</TableCell>
                      <TableCell className="text-amber-600">{record.overtime_hours || '-'}h</TableCell>
                      <TableCell>
                        <Badge className={record.status === 'clocked_in' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
                          {record.status === 'clocked_in' ? 'Working' : 'Complete'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {myRecords.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No attendance records for this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Staff Tab */}
        {canViewAll && (
          <TabsContent value="all" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <Label>Month</Label>
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((m, i) => (
                          <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2024">2024</SelectItem>
                        <SelectItem value="2025">2025</SelectItem>
                        <SelectItem value="2026">2026</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Staff</Label>
                    <Select value={selectedStaff || "all"} onValueChange={(v) => setSelectedStaff(v === "all" ? "" : v)}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="All Staff" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Staff</SelectItem>
                        {staffList.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.first_name} {s.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Search</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        className="pl-9"
                        placeholder="Search by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  {isSuperAdmin && (
                    <Button onClick={() => { fetchStaffPermissions(); setShowPermissionsDialog(true); }} variant="outline">
                      <Users className="w-4 h-4 mr-2" />
                      Manage Permissions
                    </Button>
                  )}
                  {permissions.can_manual_entry && (
                    <Button onClick={() => setShowManualEntry(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Manual Entry
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* All Records Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Clock In</TableHead>
                      <TableHead>Clock Out</TableHead>
                      <TableHead>Break</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Overtime</TableHead>
                      <TableHead>Status</TableHead>
                      {isSuperAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{record.staff_name}</p>
                            <p className="text-xs text-slate-500">{record.staff_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{record.date}</TableCell>
                        <TableCell>{formatTime(record.clock_in_time)}</TableCell>
                        <TableCell>{formatTime(record.clock_out_time)}</TableCell>
                        <TableCell>{record.break_minutes}m</TableCell>
                        <TableCell>{record.total_hours || '-'}h</TableCell>
                        <TableCell className="text-amber-600">{record.overtime_hours || '-'}h</TableCell>
                        <TableCell>
                          <Badge className={record.status === 'clocked_in' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
                            {record.status === 'clocked_in' ? 'Working' : 'Complete'}
                          </Badge>
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedRecord(record);
                                  setShowEditDialog(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(record.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {filteredRecords.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isSuperAdmin ? 9 : 8} className="text-center py-8 text-slate-500">
                          No attendance records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Summary Tab */}
        {canViewAll && (
          <TabsContent value="summary" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Summary - {months[selectedMonth - 1]} {selectedYear}</CardTitle>
                <CardDescription>Hours worked by each staff member</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff Member</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Days Worked</TableHead>
                      <TableHead className="text-right">Total Hours</TableHead>
                      <TableHead className="text-right">Overtime</TableHead>
                      <TableHead className="text-right">Avg Hours/Day</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffSummaries.map((summary) => (
                      <TableRow key={summary.staff_id}>
                        <TableCell className="font-medium">{summary.staff_name}</TableCell>
                        <TableCell className="text-slate-500">{summary.staff_email}</TableCell>
                        <TableCell className="text-right">{summary.days_worked}</TableCell>
                        <TableCell className="text-right font-medium">{summary.total_hours}h</TableCell>
                        <TableCell className="text-right text-amber-600">{summary.total_overtime}h</TableCell>
                        <TableCell className="text-right">
                          {summary.days_worked > 0 
                            ? (summary.total_hours / summary.days_worked).toFixed(1) 
                            : 0}h
                        </TableCell>
                      </TableRow>
                    ))}
                    {staffSummaries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          No attendance data for this period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Edit Dialog - Super Admin Only */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Attendance Record</DialogTitle>
            <DialogDescription>
              Modify attendance record for {selectedRecord?.staff_name}
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Clock In Time</Label>
                  <Input
                    type="datetime-local"
                    value={selectedRecord.clock_in_time?.slice(0, 16) || ''}
                    onChange={(e) => setSelectedRecord({
                      ...selectedRecord,
                      clock_in_time: e.target.value
                    })}
                  />
                </div>
                <div>
                  <Label>Clock Out Time</Label>
                  <Input
                    type="datetime-local"
                    value={selectedRecord.clock_out_time?.slice(0, 16) || ''}
                    onChange={(e) => setSelectedRecord({
                      ...selectedRecord,
                      clock_out_time: e.target.value
                    })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Break (mins)</Label>
                  <Input
                    type="number"
                    value={selectedRecord.break_minutes || 0}
                    onChange={(e) => setSelectedRecord({
                      ...selectedRecord,
                      break_minutes: parseInt(e.target.value) || 0
                    })}
                  />
                </div>
                <div>
                  <Label>Total Hours</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={selectedRecord.total_hours || 0}
                    onChange={(e) => setSelectedRecord({
                      ...selectedRecord,
                      total_hours: parseFloat(e.target.value) || 0
                    })}
                  />
                </div>
                <div>
                  <Label>Overtime Hours</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={selectedRecord.overtime_hours || 0}
                    onChange={(e) => setSelectedRecord({
                      ...selectedRecord,
                      overtime_hours: parseFloat(e.target.value) || 0
                    })}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <ManualEntryDialog 
        open={showManualEntry}
        onOpenChange={setShowManualEntry}
        staffList={staffList}
        api={api}
        onSuccess={fetchData}
        currentUser={user}
        isSuperAdmin={isSuperAdmin}
      />

      {/* Quick Clock Out Dialog */}
      <Dialog open={showClockOutDialog} onOpenChange={setShowClockOutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="w-5 h-5 text-red-600" />
              Clock Out
            </DialogTitle>
            <DialogDescription>
              Confirm your clock out and add any break time
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600">Clocked in since:</p>
              <p className="font-semibold text-lg">
                {clockStatus?.clock_in_time && new Date(clockStatus.clock_in_time).toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              <p className="text-sm text-green-600 mt-1">
                Working time: {formatElapsedTime(clockStatus?.elapsed_hours)}
              </p>
            </div>

            <div>
              <Label>Break time taken (minutes)</Label>
              <Input
                type="number"
                min="0"
                max="240"
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter total break time (lunch, tea, etc.)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClockOutDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleQuickClockOut}
              disabled={clockingOut}
              className="bg-red-600 hover:bg-red-700"
            >
              {clockingOut ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <LogOut className="w-4 h-4 mr-2" />
              )}
              Confirm Clock Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Clock Out Dialog - for HR/Admin/Directors */}
      <Dialog open={showManualClockOutDialog} onOpenChange={setShowManualClockOutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <LogOut className="w-5 h-5" />
              Manual Clock Out
            </DialogTitle>
            <DialogDescription>
              Clock out a staff member who missed or was unable to clock out themselves
            </DialogDescription>
          </DialogHeader>

          {selectedStaffForClockOut && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-medium text-slate-900">{selectedStaffForClockOut.staff_name}</p>
                <p className="text-sm text-slate-500">
                  Clocked in: {selectedStaffForClockOut.clock_in_time ? new Date(selectedStaffForClockOut.clock_in_time).toLocaleString('en-GB') : 'N/A'}
                </p>
                <p className="text-sm text-green-600">
                  Time worked so far: {formatElapsedTime(selectedStaffForClockOut.elapsed_hours)}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="manual-break">Break Time (minutes)</Label>
                <Input
                  id="manual-break"
                  type="number"
                  min="0"
                  max="120"
                  value={manualClockOutBreakMins}
                  onChange={(e) => setManualClockOutBreakMins(parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="manual-reason">Reason for Manual Clock Out *</Label>
                <Input
                  id="manual-reason"
                  value={manualClockOutReason}
                  onChange={(e) => setManualClockOutReason(e.target.value)}
                  placeholder="e.g., Staff forgot to clock out, left early due to emergency"
                />
                <p className="text-xs text-slate-500">
                  This will be recorded in the audit log
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualClockOutDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleManualClockOut}
              disabled={processingManualClockOut || !manualClockOutReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {processingManualClockOut ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4 mr-2" />
                  Clock Out Staff
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Management Dialog - Super Admin Only */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1e3a5f]" />
              Staff Access Management
            </DialogTitle>
            <DialogDescription>
              View and manage staff access permissions. Super Admin has full access to all features.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff Member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-center">Manual Entry</TableHead>
                  <TableHead className="text-center">View All Records</TableHead>
                  <TableHead className="text-center">Excel Download</TableHead>
                  <TableHead className="text-right">Reset</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staffPermissions.map((staff) => (
                  <TableRow key={staff.user_id} className={staff.is_super_admin ? 'bg-amber-50' : ''}>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{staff.name}</p>
                          {staff.is_super_admin && (
                            <Badge className="bg-amber-500 text-white text-xs">Super Admin</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">{staff.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {staff.role?.replace(/_/g, ' ') || 'N/A'}
                      </Badge>
                    </TableCell>
                    {/* Manual Entry Permission */}
                    <TableCell className="text-center">
                      {staff.is_super_admin ? (
                        <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3" /></Badge>
                      ) : (
                        <Button
                          variant={staff.can_manual_entry ? "default" : "outline"}
                          size="sm"
                          className={staff.can_manual_entry ? "bg-green-500 hover:bg-green-600" : ""}
                          onClick={() => toggleStaffPermission(staff.user_id, 'manual_entry', staff.can_manual_entry)}
                          disabled={updatingPermission === `${staff.user_id}-manual_entry`}
                        >
                          {updatingPermission === `${staff.user_id}-manual_entry` ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : staff.can_manual_entry ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : (
                            <span className="text-xs">Grant</span>
                          )}
                        </Button>
                      )}
                    </TableCell>
                    {/* View All Records Permission */}
                    <TableCell className="text-center">
                      {staff.is_super_admin ? (
                        <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3" /></Badge>
                      ) : (
                        <Button
                          variant={staff.can_view_all_records ? "default" : "outline"}
                          size="sm"
                          className={staff.can_view_all_records ? "bg-blue-500 hover:bg-blue-600" : ""}
                          onClick={() => toggleStaffPermission(staff.user_id, 'view_all', staff.can_view_all_records)}
                          disabled={updatingPermission === `${staff.user_id}-view_all`}
                        >
                          {updatingPermission === `${staff.user_id}-view_all` ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : staff.can_view_all_records ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : (
                            <span className="text-xs">Grant</span>
                          )}
                        </Button>
                      )}
                    </TableCell>
                    {/* Excel Download Permission */}
                    <TableCell className="text-center">
                      {staff.is_super_admin ? (
                        <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3" /></Badge>
                      ) : (
                        <Button
                          variant={staff.can_excel_download ? "default" : "outline"}
                          size="sm"
                          className={staff.can_excel_download ? "bg-purple-500 hover:bg-purple-600" : ""}
                          onClick={() => toggleStaffPermission(staff.user_id, 'excel', staff.can_excel_download)}
                          disabled={updatingPermission === `${staff.user_id}-excel`}
                        >
                          {updatingPermission === `${staff.user_id}-excel` ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : staff.can_excel_download ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : (
                            <span className="text-xs">Grant</span>
                          )}
                        </Button>
                      )}
                    </TableCell>
                    {/* Reset Actions */}
                    <TableCell className="text-right">
                      {staff.is_super_admin ? (
                        <span className="text-xs text-slate-500 italic">Full Access</span>
                      ) : staff.has_override ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => clearPermissionOverride(staff.user_id)}
                          disabled={updatingPermission?.startsWith(staff.user_id)}
                          title="Reset to role default"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400">Default</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remote Work Request Dialog */}
      <Dialog open={showRemoteRequestDialog} onOpenChange={setShowRemoteRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Remote Work Approval</DialogTitle>
            <DialogDescription>
              Submit a request to work from home. This requires pre-approval from a Director or Clinical Lead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Remote Work *</Label>
              <Input
                placeholder="e.g., Medical appointment, childcare, home renovation..."
                value={remoteRequestReason}
                onChange={(e) => setRemoteRequestReason(e.target.value)}
              />
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Your request will be reviewed by a Director or Clinical Lead. You will be notified once approved.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoteRequestDialog(false)}>Cancel</Button>
            <Button onClick={submitRemoteRequest} disabled={submittingRemoteRequest || !remoteRequestReason.trim()}>
              {submittingRemoteRequest ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Remote Requests Dialog (For Approvers) */}
      <Dialog open={showPendingRequestsDialog} onOpenChange={setShowPendingRequestsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Remote Work Approvals</DialogTitle>
            <DialogDescription>
              Review and approve/reject remote work requests from staff members.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {pendingRemoteRequests.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-300 mb-3" />
                <p className="text-slate-600 font-medium">No Pending Requests</p>
                <p className="text-sm text-slate-500 mt-1">All remote work requests have been processed.</p>
              </div>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    You have <strong>{pendingRemoteRequests.length}</strong> pending request(s) to review
                  </p>
                </div>
                {pendingRemoteRequests.map((req) => (
                  <Card key={req.id} className="border-slate-200">
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">{req.user_name}</p>
                          <p className="text-sm text-slate-500">{req.user_email}</p>
                          <Badge variant="outline" className="mt-1">{req.user_role?.replace(/_/g, ' ')}</Badge>
                          <p className="mt-2 text-sm"><strong>Reason:</strong> {req.reason}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Requested: {new Date(req.requested_at).toLocaleString('en-GB')}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => handleRemoteRequestAction(req.id, 'reject')}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleRemoteRequestAction(req.id, 'approve')}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPendingRequestsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Manual Entry Dialog Component
const ManualEntryDialog = ({ open, onOpenChange, staffList, api, onSuccess, currentUser, isSuperAdmin }) => {
  const [formData, setFormData] = useState({
    staff_id: currentUser?.id || '',
    date: new Date().toISOString().slice(0, 10),
    clock_in_time: '',
    clock_out_time: '',
    break_minutes: 0
  });
  const [submitting, setSubmitting] = useState(false);

  // Reset staff_id to current user when dialog opens (for non-super-admin)
  useEffect(() => {
    if (open && !isSuperAdmin && currentUser?.id) {
      setFormData(prev => ({ ...prev, staff_id: currentUser.id }));
    }
  }, [open, isSuperAdmin, currentUser?.id]);

  const handleSubmit = async () => {
    if (!formData.staff_id || !formData.date || !formData.clock_in_time || !formData.clock_out_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/attendance/manual-entry', {
        staff_id: formData.staff_id,
        date: formData.date,
        clock_in_time: `${formData.date}T${formData.clock_in_time}:00Z`,
        clock_out_time: `${formData.date}T${formData.clock_out_time}:00Z`,
        break_minutes: formData.break_minutes
      });
      toast.success('Manual entry created');
      onOpenChange(false);
      setFormData({
        staff_id: isSuperAdmin ? '' : currentUser?.id || '',
        date: new Date().toISOString().slice(0, 10),
        clock_in_time: '',
        clock_out_time: '',
        break_minutes: 0
      });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create entry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Manual Attendance Entry</DialogTitle>
          <DialogDescription>
            {isSuperAdmin 
              ? 'Add an attendance record for any staff member' 
              : 'Add a manual attendance record for yourself'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {isSuperAdmin ? (
            <div>
              <Label>Staff Member *</Label>
              <Select value={formData.staff_id} onValueChange={(v) => setFormData({ ...formData, staff_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staffList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="bg-slate-50 p-3 rounded-lg">
              <Label className="text-slate-600">Creating entry for:</Label>
              <p className="font-medium">{currentUser?.first_name} {currentUser?.last_name}</p>
            </div>
          )}
          <div>
            <Label>Date *</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Clock In Time *</Label>
              <Input
                type="time"
                value={formData.clock_in_time}
                onChange={(e) => setFormData({ ...formData, clock_in_time: e.target.value })}
              />
            </div>
            <div>
              <Label>Clock Out Time *</Label>
              <Input
                type="time"
                value={formData.clock_out_time}
                onChange={(e) => setFormData({ ...formData, clock_out_time: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Break Time (minutes)</Label>
            <Input
              type="number"
              min="0"
              value={formData.break_minutes}
              onChange={(e) => setFormData({ ...formData, break_minutes: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttendancePage;
